<script>
  import DataTableBlack from "@components/DataTableBlack.svelte";
  import Modal from "@components/Modal.svelte";
  import { Button, InfoBlock, TextInput, Tooltip } from "paxray-components-svelte";
  import { fetchWrapper } from "lib/util/fetchWrapper.js";
  import {
    findCustomMatch,
    findPresetMatch,
    findPresetSuggestions,
    isSubUrlOfWebsite,
    isValidWebsite,
    presetUrlToDomain,
    websiteDisplayName,
    websiteMatches,
    websitePattern,
  } from "lib/util/restrictionRules.js";

  export let currentStep, stepsCompleted, idproject;

  let submitModalOpen = false;
  // Set by the backend once "Complete & Lock Exclusions" is done for the project.
  export let closed = false;
  let blacklistWebsites = [];
  let newItemModalOpen = false;
  let currentWebsite = {};
  let currentlistid = 0;
  let editId = -1;
  let expandedRowIds = [];
  let showHowto = false;
  let howtoStep = 0;

  const howtoSteps = [
    {
      image: "img/userinput/websites/screen1.png",
      text: "Open the website in your browser",
    },
    {
      image: "img/userinput/websites/screen2.png",
      text: "Copy the address from the address bar, e.g. amazon.com",
    },
    {
      image: "img/userinput/websites/screen3.png",
      text: "To exclude only one page, copy its full address as Sub-URL, e.g. amazon.com/music",
    },
  ];

  $: isLastHowtoStep = howtoStep === howtoSteps.length - 1;

  function openHowto() {
    howtoStep = 0;
    showHowto = true;
  }

  function nextHowtoStep() {
    if (isLastHowtoStep) showHowto = false;
    else howtoStep += 1;
  }

  function previousHowtoStep() {
    if (howtoStep === 0) showHowto = false;
    else howtoStep -= 1;
  }

  fetchWrapper(
    `/api/damprojects/getUserLimitations/Blacklist?idproject=${encodeURIComponent(idproject)}`,
  )
    .then((response) => response.json())
    .then((data) => {
      closed = data.closed === true;
      blacklistWebsites = normalizeRows(data.webapp);
      // Open the row when it is empty, so the hint is visible right away.
      if (blacklistWebsites.every((row) => row.user.length === 0)) {
        expandedRowIds = ["project-website-exclusions"];
      }
    });

  function normalizeRows(rows) {
    return (rows || []).map((row) => ({
      ...row,
      user: Array.isArray(row.user) ? row.user : [],
      preset: Array.isArray(row.preset) ? row.preset : [],
    }));
  }

  function addToBlacklist(id = 0) {
    if (!blacklistWebsites[id]) return;
    newItemModalOpen = true;
    currentlistid = id;
    editId = -1;
    urlerror = false;
    suburlerror = false;
    showHowto = false;
    currentWebsite = {
      limitationPresetId: blacklistWebsites[id]["id"],
      editable: true,
      id: blacklistWebsites[id].user.length,
      name: "",
      url: "",
      suburl: "",
      reason: "",
    };
  }

  let urlerror = false;
  let suburlerror = false;
  // Errors disappear as soon as their own field is changed. Separate values, so typing
  // in one field does not clear the error of the other.
  $: urlValue = currentWebsite.url;
  $: suburlValue = currentWebsite.suburl;
  $: urlValue, (urlerror = false);
  $: suburlValue, (suburlerror = false);

  // Checked when the field is left, an empty address only on save.
  function validateUrl() {
    urlerror =
      !!currentWebsite.url && !presetMatch && !isValidWebsite(currentWebsite.url);
  }

  function validateSubUrl() {
    suburlerror = !isSubUrlOfWebsite(currentWebsite.suburl, currentWebsite.url);
  }

  $: presetMatch = findPresetMatch(
    blacklistWebsites,
    currentWebsite.url,
    websiteMatches,
  );
  $: customDuplicate = findCustomMatch(
    blacklistWebsites,
    currentWebsite.url,
    websiteMatches,
    { listId: currentlistid, ruleId: editId },
  );
  $: websiteSuggestions = findPresetSuggestions(
    blacklistWebsites,
    currentWebsite.url,
    (preset) => presetUrlToDomain(preset.url),
  );

  function applySuggestion(suggestion) {
    currentWebsite.url = suggestion.value;
    currentWebsite.name = suggestion.value;
  }

  // Save stays locked while the entry cannot be saved, the tooltip names the reason.
  $: saveBlockedReason = !currentWebsite.url
    ? "Enter the website address first."
    : customDuplicate
      ? "This website is already in your custom rules."
      : !presetMatch && !isValidWebsite(currentWebsite.url)
        ? "Enter a valid website address, e.g. amazon.com"
        : !isSubUrlOfWebsite(currentWebsite.suburl, currentWebsite.url)
          ? "The sub-URL must start with the website address."
          : "";

  function saveNewWebsite() {
    // Suggestions come from the preset lists and are always valid.
    urlerror =
      !currentWebsite.url || (!presetMatch && !isValidWebsite(currentWebsite.url));
    suburlerror = !isSubUrlOfWebsite(currentWebsite.suburl, currentWebsite.url);
    if (urlerror || suburlerror || customDuplicate) return;

    currentWebsite.name =
      currentWebsite.name || websiteDisplayName(currentWebsite.url);
    currentWebsite.suburl = currentWebsite.suburl || "";
    currentWebsite.reason = currentWebsite.reason || "";

    if (editId == -1)
      blacklistWebsites[currentlistid]["user"].push(currentWebsite);
    else blacklistWebsites[currentlistid]["user"][editId] = currentWebsite;
    blacklistWebsites = blacklistWebsites;
    newItemModalOpen = false;
    expandedRowIds = ["project-website-exclusions"];
  }

  function editUserWebsite(listId, id) {
    currentlistid = listId;
    editId = id;
    urlerror = false;
    suburlerror = false;
    showHowto = false;
    currentWebsite = blacklistWebsites[currentlistid]["user"][editId];
    newItemModalOpen = true;
  }

  function deleteFromBlackList(list, id) {
    list.splice(id, 1);
    let indexcounter = 0;
    list.forEach((element) => {
      element.id = indexcounter;
      indexcounter++;
    });
    list = list;
    blacklistWebsites = blacklistWebsites;
  }

  function submitBlacklist() {
    // The recorder reads urls as patterns, the list keeps the plain addresses
    const payload = blacklistWebsites.map((list) => ({
      ...list,
      user: (list.user || []).map((rule) => ({
        ...rule,
        url: websitePattern(rule.url),
        suburl: websitePattern(rule.suburl),
      })),
    }));
    fetchWrapper(`/api/damprojects/saveUserLimitations/${idproject}/black`, {
      method: "POST",
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => {
        // Locked while this page was open: show the closed hint instead of an error.
        if (data.status == "closed") {
          closed = true;
          submitModalOpen = false;
          expandedRowIds = ["project-website-exclusions"];
          return;
        }
        if (data.status == "success") {
          currentStep = 2;
          stepsCompleted = true;
          submitModalOpen = false;
        }
      });
  }

  $: customRows = blacklistWebsites.flatMap((row, sourceIndex) =>
    (row.user || []).map((rule) => ({
      ...rule,
      id: `${sourceIndex}-${rule.id}`,
      sourceIndex,
      sourceId: rule.id,
    })),
  );
  $: projectRows = [
    {
      id: "project-website-exclusions",
      name: "Custom Website Exclusions",
      description:
        "Add websites or web apps that should be excluded for this project.",
      edit: true,
    },
  ];
</script>

<Modal
  bind:open={submitModalOpen}
  modalHeading="Submit Entries"
  primaryButtonText="Submit Excluded Websites"
  secondaryButtonText="Cancel"
  secondaryButtonAction={() => (submitModalOpen = false)}
  primaryButtonAction={submitBlacklist}
  on:close={() => (submitModalOpen = false)}
  actions
  autowidth
>
  <p>Do you want to submit your entries?</p>
  <p>This is a permanent action and cannot be undone.</p>
</Modal>

<DataTableBlack
  expandable
  headers={[
    { key: "name", value: "Name" },
    { key: "description", value: "Description" },
    { key: "edit", value: "Actions" },
  ]}
  rows={projectRows}
  bind:expandedRowIds
  showToolbar={false}
  showPagination={false}
>
  <svelte:fragment slot="cell" let:cell let:row>
    {#if cell.key == "edit"}
      <div class="action_cell">
        {#if closed}
          <Tooltip hideIcon direction="left">
            <span slot="triggerText" class="closed_button">
              <Button kind="secondary" size="field" text="Add Website" disabled />
            </span>
            <span class="closed_tooltip"
              >{"Exclusions for this project are closed.\nContact your project lead if a website is missing."}</span
            >
          </Tooltip>
        {:else}
          <Button
            kind="secondary"
            size="field"
            text="Add Website"
            on:click={() => addToBlacklist(0)}
          />
        {/if}
      </div>
    {:else}
      {cell.value}
    {/if}
  </svelte:fragment>
  <svelte:fragment slot="expanded-row" let:row>
    {#if closed}
      <p class="empty_hint">
        Exclusions for this project are closed. Contact your project lead if a website is missing.
      </p>
    {:else if customRows.length === 0}
      <p class="empty_hint">
        You have not added any custom websites yet. Click Add Website to exclude your own websites.
      </p>
    {:else}
      <DataTableBlack
        headers={[
          { key: "name", value: "Name" },
          { key: "url", value: "URL" },
          { key: "suburl", value: "Sub-URL" },
          { key: "reason", value: "Reason" },
          { key: "editable", value: "Actions" },
        ]}
        rows={customRows}
        showToolbar={false}
        showPagination={false}
      >
        <svelte:fragment slot="cell" let:cell let:row={innerrow}>
          {#if cell.key == "editable"}
            <div class="action_cell">
              <Button
                kind="secondary"
                size="field"
                text="Edit"
                on:click={() => {
                  editUserWebsite(innerrow.sourceIndex, innerrow.sourceId);
                }}
              />
              <Button
                kind="secondary"
                size="field"
                text="Delete"
                on:click={() => {
                  deleteFromBlackList(
                    blacklistWebsites[innerrow.sourceIndex].user,
                    innerrow.sourceId,
                  );
                }}
              />
            </div>
          {:else}
            {cell.value || ""}
          {/if}
        </svelte:fragment>
      </DataTableBlack>
    {/if}
  </svelte:fragment>
</DataTableBlack>

{#if !closed}
<div class="button_wrapper" data-guide="restrictions-submit">
  <div></div>
  <Button
    kind="secondary"
    text="Submit Excluded Websites"
    on:click={() => (submitModalOpen = true)}
  />
</div>
{/if}

<Modal
  bind:open={newItemModalOpen}
  modalHeading={showHowto ? "How to find the URL" : "Add Website"}
  preventCloseOnClickOutside
  noSecondaryButton
  primaryButtonText={showHowto ? (isLastHowtoStep ? "Got it" : "Next") : "Save"}
  primaryButtonAction={showHowto ? nextHowtoStep : saveNewWebsite}
  primaryButtonValidations={!showHowto && !!saveBlockedReason}
  primaryButtonDisabledTooltip={saveBlockedReason}
  primaryButtonVariant="secondary"
  on:close={() => (newItemModalOpen = false)}
  width="auto"
  actions
  xxl
>
  {#if showHowto}
    <div class="howto">
      <img
        class="howto_image"
        src={howtoSteps[howtoStep].image}
        alt={howtoSteps[howtoStep].text}
      />
      <div class="howto_text">
        <p class="howto_step">Step {howtoStep + 1} of {howtoSteps.length}</p>
        <p>{howtoSteps[howtoStep].text}</p>
      </div>
    </div>
  {:else}
    <div class="blacklist_website_modal">
      <InfoBlock
        type="info"
        heading="Which website?"
        message="This website will be explicitly excluded from all future data collection. Enter its address or type its name to pick a suggestion."
        showClose={false}
      />

      <div class="input_wrapper">
        <div class="rule_input">
          <div class="rule_input_row">
            <div class="rule_input_field">
              <TextInput
                invalid={urlerror || customDuplicate}
                invalidText={customDuplicate
                  ? "This website is already in your custom rules"
                  : !currentWebsite.url
                    ? "Please specify the website URL"
                    : "Enter a valid website address, e.g. amazon.com"}
                labelText="Website URL (required)"
                placeholder="Enter website URL or search presets, e.g. amazon.com"
                description={presetMatch
                  ? `Also covered by the preset list ${presetMatch.group.name}. This rule keeps it excluded even if the preset is disabled.`
                  : null}
                bind:value={currentWebsite.url}
                on:blur={validateUrl}
              />
            </div>
            <div class="howto_button">
              <Button kind="secondary" on:click={openHowto}>
                How do I find the URL?
              </Button>
            </div>
          </div>
          {#if websiteSuggestions.length}
            <div class="rule_suggestions">
              {#each websiteSuggestions as suggestion}
                <Button
                  kind="secondary"
                  size="field"
                  text="{suggestion.value} ({suggestion.group})"
                  on:click={() => applySuggestion(suggestion)}
                />
              {/each}
            </div>
          {/if}
        </div>
        <TextInput
          labelText="Display Name (optional)"
          placeholder="Shown in the rule list, e.g. Amazon Music"
          bind:value={currentWebsite.name}
        />
        <TextInput
          labelText="Sub-URL (optional)"
          placeholder="Sub-URL of the page, e.g. amazon.com/music"
          invalid={suburlerror}
          invalidText="The sub-URL must start with the website address above"
          bind:value={currentWebsite.suburl}
          on:blur={validateSubUrl}
        />
        <TextInput
          labelText="Reason (optional)"
          placeholder="Short reason for this rule, e.g. music streaming"
          bind:value={currentWebsite.reason}
        />
      </div>
    </div>
  {/if}
  <div slot="left-buttons">
    {#if showHowto}
      <Button
        kind="secondary"
        text={howtoStep === 0 ? "Back to form" : "Back"}
        on:click={previousHowtoStep}
      />
    {:else}
      <Button
        kind="outline-dark"
        text="Cancel"
        on:click={() => (newItemModalOpen = false)}
      />
    {/if}
  </div>
</Modal>

<style>
  .closed_button {
    display: inline-flex;
  }

  /* Disabled buttons swallow hover in some browsers, the tooltip trigger must receive it. */
  .closed_button :global(button:disabled) {
    pointer-events: none;
  }

  .closed_tooltip {
    white-space: pre-line;
  }

  .button_wrapper {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
  }

  :global(.bx--data-table th:last-child),
  :global(.bx--data-table td:last-child) {
    padding-right: 1rem;
    text-align: right;
  }

  :global(.bx--data-table th:last-child .bx--table-header-label) {
    display: flex;
    justify-content: flex-end;
  }

  :global(.bx--data-table td:last-child .button-secondary) {
    display: flex;
  }

  /* Carbon indents expanded rows, and a global rule elsewhere resets that for
     some column counts. Reset it here so the hint padding alone decides. */
  :global(td:has(> .bx--child-row-inner-container > .empty_hint)) {
    padding-left: 0 !important;
  }

  .empty_hint {
    /* Lines up with the text of the expandable row above. */
    padding: 1rem 1rem 1rem 3.65rem;
    font: inherit;
    letter-spacing: inherit;
    color: var(--cds-text-secondary, #525252);
  }

  .action_cell {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .blacklist_website_modal > :global(* + *) {
    margin-top: var(--control-gap);
  }

  .input_wrapper {
    display: flex;
    flex-direction: column;
    background-color: var(--pax-white);
    gap: var(--control-gap);
  }

  .rule_suggestions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--control-gap);
    margin-top: var(--control-gap);
  }

  .rule_input_row {
    display: flex;
    align-items: flex-start;
    gap: var(--control-gap);
  }

  .rule_input_field {
    flex: 1;
    min-width: 0;
  }

  /* Aligns the button with the input, below the field label. */
  .howto_button {
    margin-top: 1.5rem;
  }

  .howto {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .howto_image {
    align-self: center;
    width: auto;
    max-width: 100%;
    /* Modal is capped at 84% height, 18rem covers header, step text and footer. */
    max-height: calc(84vh - 18rem);
    border: 1px solid var(--pax-black-inactive);
    border-radius: 0.5rem;
  }

  .howto_text {
    text-align: center;
  }

  .howto_text p {
    margin: 0;
  }

  .howto_step {
    font-weight: 600;
  }
</style>
